import React, { memo, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { TableCell, TableHead, TableRow } from '@mui/material';
import TooltipedSortLabel from 'app/shared/components/common/TooltipedSortLabel';
import PropTypes from 'prop-types';

import { ConditionKey } from 'constants/condition';

import { selectIsBrokerCompany } from 'store/auth/selectors';

const LoanConditionTableHead = ({
  order,
  onRequestSort,
  withDeletedDateCell,
  withRejectedDateCell,
  withSignedOffCell,
  withActionsCell,
  withCollapseCell,
}) => {
  const isBrokerCompany = useSelector(selectIsBrokerCompany);

  const columns = useMemo(
    () => [
      {
        id: ConditionKey.NUMBER,
        align: 'left',
        label: 'Number',
        sort: true,
        isRendered: !isBrokerCompany,
      },
      {
        id: ConditionKey.MESSAGE,
        align: 'left',
        label: 'Message',
        sort: true,
        isRendered: true,
      },
      {
        id: ConditionKey.INTERNAL_TEXT,
        align: 'left',
        label: 'Internal Text',
        sort: true,
        isRendered: isBrokerCompany,
      },
      {
        id: ConditionKey.PRIOR_TO_TYPE,
        align: 'left',
        label: 'Prior To',
        sort: false,
        isRendered: true,
      },
      {
        id: ConditionKey.CREATED_BY,
        align: 'left',
        label: 'Added By',
        sort: true,
        isRendered: true,
      },
      {
        id: ConditionKey.CREATED_DATE,
        align: 'left',
        label: 'Added',
        sort: true,
        isRendered: true,
      },
      {
        id: ConditionKey.SIGNED_OFF_BY,
        align: 'left',
        label: isBrokerCompany ? 'Status' : 'Signed Off By',
        sort: true,
        isRendered: withSignedOffCell,
      },
      {
        id: ConditionKey.SIGNED_OFF,
        align: 'left',
        label: 'Signed Off',
        sort: true,
        isRendered: withSignedOffCell,
      },
      {
        id: ConditionKey.LAST_MODIFIED,
        align: 'left',
        label: 'Deleted',
        sort: true,
        isRendered: withDeletedDateCell,
      },
      {
        id: ConditionKey.REJECTED_DATE,
        align: 'left',
        label: 'Rejected',
        sort: true,
        isRendered: withRejectedDateCell,
      },
      {
        id: ConditionKey.ACTIONS,
        isRendered: withActionsCell,
      },
      {
        id: ConditionKey.COLLAPSE,
        isRendered: withCollapseCell,
      },
    ],
    [
      isBrokerCompany,
      withActionsCell,
      withCollapseCell,
      withDeletedDateCell,
      withRejectedDateCell,
      withSignedOffCell,
    ],
  );

  return (
    <TableHead>
      <TableRow className="h-64 uppercase">
        {columns.map(({ align, id, disablePadding, sort, label, className, isRendered }) => {
          if (!isRendered) {
            return null;
          }

          return (
            <TableCell
              align={align}
              className={className}
              key={id}
              padding={disablePadding ? 'none' : 'default'}
              sortDirection={order.id === id ? order.direction : false}
            >
              <TooltipedSortLabel
                align={align}
                data-testid={id}
                id={id}
                isSort={sort}
                label={label}
                order={order}
                onRequestSort={onRequestSort}
              />
            </TableCell>
          );
        })}
      </TableRow>
    </TableHead>
  );
};

LoanConditionTableHead.propTypes = {
  order: PropTypes.object.isRequired,
  withActionsCell: PropTypes.bool.isRequired,
  withCollapseCell: PropTypes.bool.isRequired,
  withDeletedDateCell: PropTypes.bool.isRequired,
  withRejectedDateCell: PropTypes.bool.isRequired,
  withSignedOffCell: PropTypes.bool.isRequired,
  onRequestSort: PropTypes.func.isRequired,
};

export default memo(LoanConditionTableHead);
